import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { downloadCsv } from "../utils/csv";
import { useToast } from "../components/ToastProvider";
import { Eye } from "lucide-react";
import BookingDetailsModal from "../components/BookingDetailsModal";

const defaultFilters = {
  client: "",
  status: "all",
  assigned: "all",
  employeeId: "",
  dateFrom: "",
  dateTo: "",
};

export default function Bookings() {
  const toast = useToast();
  const [filters, setFilters] = useState(defaultFilters);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsBooking, setDetailsBooking] = useState(null);

  const loadEmployees = async () => {
    try {
      const response = await api.get("/api/admin/employees");
      setEmployees(response?.data?.data || []);
    } catch {
      setEmployees([]);
    }
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
      };
      if (params.status === "all") delete params.status;
      if (params.assigned === "all") delete params.assigned;
      if (!params.employeeId) delete params.employeeId;
      if (!params.dateFrom) delete params.dateFrom;
      if (!params.dateTo) delete params.dateTo;
      if (!params.client) delete params.client;

      const response = await api.get("/api/admin/bookings", { params });
      setRows(response?.data?.data || []);
      setTotal(Number(response?.data?.total || 0));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetails = async (serviceId) => {
    if (!serviceId) return;
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError("");
    setDetailsBooking(null);

    try {
      const response = await api.get(`/api/admin/bookings/${serviceId}`);
      setDetailsBooking(response?.data?.data || null);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to load booking details";
      setDetailsError(message);
      toast.error(message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsLoading(false);
    setDetailsError("");
    setDetailsBooking(null);
  };

  const saveDetails = async (payload) => {
    if (!detailsBooking?.serviceId) throw new Error("No booking selected");

    const response = await api.put(`/api/admin/bookings/${detailsBooking.serviceId}`, payload);
    const updated = response?.data?.data || null;
    if (updated) {
      setDetailsBooking(updated);
      setRows((prev) =>
        prev.map((row) => (Number(row.serviceId) === Number(updated.serviceId) ? { ...row, ...updated } : row))
      );
    }
    toast.success("Booking updated.");
    await loadBookings();
    return updated;
  };

  const csvRows = useMemo(
    () =>
      rows.map((r) => ({
        serviceId: r.serviceId,
        step: r.step,
        paymentStatus: Number(r.step) === 3 ? "Completed" : "Incomplete",
        client: `${r.firstName || ""} ${r.lastName || ""}`.trim(),
        email: r.email,
        phone: r.phone,
        bedrooms: r.bedrooms,
        bathrooms: r.bathrooms,
        preferredDate: r.preferredDate,
        preferredTime: r.preferredTime,
        address: [r.streetAddress, r.suburb, r.postcode].filter(Boolean).join(", "),
        getInOption: r.getInOption || "",
        parkOption: r.parkOption || "",
        charge: r.charge ?? r.Charge ?? "",
        cleaningHoursApprox: r.cleaningHoursApprox ?? "",
        assignedEmployee: r.employeeName || "",
        jobStatus: r.jobStatus || "",
        creationDate: r.creationDate,
      })),
    [rows]
  );

  const update = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const reset = () => setFilters(defaultFilters);

  return (
    <div className="rs-page">
      <div className="rs-page__header">
        <div>
          <h1 className="rs-h1">Bookings</h1>
          <div className="rs-muted">
            Step 3 = payment completed, Step 2 = incomplete payment
          </div>
        </div>
        <button
          className="rs-btn rs-btn--outline"
          onClick={() => downloadCsv("bookings.csv", csvRows)}
          disabled={csvRows.length === 0}
        >
          Download CSV
        </button>
      </div>

      <div className="rs-card rs-filters">
        <div className="rs-filters__grid">
          <label className="rs-field">
            <span>Client (name/email/phone)</span>
            <input
              className="rs-input"
              name="client"
              value={filters.client}
              onChange={update}
              placeholder="Search..."
            />
          </label>

          <label className="rs-field">
            <span>Payment status</span>
            <select className="rs-input" name="status" value={filters.status} onChange={update}>
              <option value="all">All</option>
              <option value="paid">Paid (Step 3)</option>
              <option value="incomplete">Incomplete (Step 2)</option>
            </select>
          </label>

          <label className="rs-field">
            <span>Assignment</span>
            <select className="rs-input" name="assigned" value={filters.assigned} onChange={update}>
              <option value="all">All</option>
              <option value="true">Assigned</option>
              <option value="false">Unassigned</option>
            </select>
          </label>

          <label className="rs-field">
            <span>Employee</span>
            <select
              className="rs-input"
              name="employeeId"
              value={filters.employeeId}
              onChange={update}
            >
              <option value="">All</option>
              {employees.map((e) => (
                <option key={e.empid} value={e.empid}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>

          <label className="rs-field">
            <span>Date from</span>
            <input className="rs-input" type="date" name="dateFrom" value={filters.dateFrom} onChange={update} />
          </label>

          <label className="rs-field">
            <span>Date to</span>
            <input className="rs-input" type="date" name="dateTo" value={filters.dateTo} onChange={update} />
          </label>
        </div>

        <div className="rs-filters__actions">
          <button className="rs-btn rs-btn--primary" onClick={loadBookings} disabled={loading}>
            {loading ? "Loading..." : "Apply filters"}
          </button>
          <button className="rs-btn rs-btn--ghost" onClick={reset} disabled={loading}>
            Reset
          </button>
        </div>
      </div>

      <div className="rs-card">
        <div className="rs-card__header rs-card__header--row">
          <div className="rs-card__title">All bookings</div>
          <div className="rs-muted">{total} records</div>
        </div>

        <div className="rs-table-wrap">
          <table className="rs-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>
                <th>Client</th>
                <th>Contact</th>
                <th>Preferred</th>
                <th>Get In</th>
                <th>Parking</th>
                <th>Bathrooms</th>
                <th>Charge</th>
                <th>Cleaning Hrs (Aprox.)</th>
                <th>Employee</th>
                <th>Job</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="rs-empty">
                      {loading ? "Loading..." : "No bookings found"}
                    </td>
                  </tr>
                ) : (
                rows.map((r) => (
                  <tr key={r.serviceId}>
                    <td data-label="ID">
                      <div className="rs-td__value">{r.serviceId}</div>
                    </td>
                    <td data-label="Status">
                      <div className="rs-td__value">
                        {Number(r.step) === 3 ? (
                          <span className="rs-pill rs-pill--success">Paid</span>
                        ) : (
                          <span className="rs-pill rs-pill--warning">Incomplete</span>
                        )}
                      </div>
                    </td>
                    <td data-label="Client">
                      <div className="rs-td__value">{`${r.firstName || ""} ${r.lastName || ""}`.trim()}</div>
                    </td>
                    <td data-label="Contact">
                      <div className="rs-td__value rs-cell--stack">
                        <div>{r.email}</div>
                        <div className="rs-muted">{r.phone}</div>
                      </div>
                    </td>
                    <td data-label="Preferred">
                      <div className="rs-td__value">
                        {r.preferredDate ? (
                          <div className="rs-cell--stack">
                            <div>{r.preferredDate}</div>
                            <div className="rs-muted">{r.preferredTime || ""}</div>
                          </div>
                        ) : (
                          "-"
                        )}
                      </div>
                    </td>
                    <td data-label="Get In">
                      <div className="rs-td__value">{r.getInOption || <span className="rs-muted">-</span>}</div>
                    </td>
                    <td data-label="Parking">
                      <div className="rs-td__value">{r.parkOption || <span className="rs-muted">-</span>}</div>
                    </td>
                    <td data-label="Bathrooms">
                      <div className="rs-td__value">
                        {typeof r.bathrooms !== "undefined" && r.bathrooms !== null ? r.bathrooms : <span className="rs-muted">-</span>}
                      </div>
                    </td>
                    <td data-label="Charge">
                      <div className="rs-td__value">
                        {(() => {
                          const raw = r.charge ?? r.Charge;
                          if (raw === null || typeof raw === "undefined" || raw === "") return <span className="rs-muted">-</span>;
                          const value = Number(raw);
                          return Number.isFinite(value) ? `$${value.toFixed(2)}` : String(raw);
                        })()}
                      </div>
                    </td>
                    <td data-label="Cleaning Hrs (Aprox.)">
                      <div className="rs-td__value">
                        {r.cleaningHoursApprox ? `${r.cleaningHoursApprox} hrs` : <span className="rs-muted">-</span>}
                      </div>
                    </td>
                    <td data-label="Employee">
                      <div className="rs-td__value">
                        {r.employeeName || <span className="rs-muted">Unassigned</span>}
                      </div>
                    </td>
                    <td data-label="Job">
                      <div className="rs-td__value">{r.jobStatus || <span className="rs-muted">-</span>}</div>
                    </td>
                    <td data-label="View">
                      <div className="rs-td__value">
                        <button
                          type="button"
                          className="rs-icon-btn rs-icon-btn--sm"
                          onClick={() => openDetails(r.serviceId)}
                          aria-label={`View booking ${r.serviceId}`}
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BookingDetailsModal
        open={detailsOpen}
        booking={detailsBooking}
        loading={detailsLoading}
        error={detailsError}
        onClose={closeDetails}
        onSave={saveDetails}
      />
    </div>
  );
}
